/**
 * Absence calls (Phase 2b). M1: my history, someone's timeline (managers), the today list and
 * the catalogue. M2: recording, closing, editing, cancelling and the return-to-work form;
 * requests and withdraw; the approvals list and decisions; the RTW-overdue and fit-notes-due
 * lists. Every mutation invalidates absence, holidays (returned days), Home's tasks and the
 * bell so each screen redraws from the API's answer.
 */
import {
  AbsenceApprovalDetailSchema,
  AbsenceApprovalsResponseSchema,
  AbsenceAttentionResponseSchema,
  AbsenceDecisionResponseSchema,
  AbsenceRequestResponseSchema,
  AbsenceTimelineSchema,
  AbsenceTodayResponseSchema,
  AbsenceTypesResponseSchema,
  AbsenceWriteResponseSchema,
  HolidayOverlapPreviewSchema,
  AbsenceWithdrawResponseSchema,
  MyAbsenceResponseSchema,
  type AbsenceApprovalDetail,
  type AbsenceApprovalsResponse,
  type AbsenceAttentionResponse,
  type AbsenceClose,
  type AbsenceDecisionResponse,
  type AbsencePatch,
  type AbsenceRecordCreate,
  type AbsenceRequestCreate,
  type AbsenceRequestResponse,
  type AbsenceTimeline,
  type AbsenceTodayResponse,
  type AbsenceTypesResponse,
  type AbsenceWriteResponse,
  type HolidayOverlapPreview,
  type MyAbsenceResponse,
  type ReturnToWorkPut,
} from '@aroo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, apiPut } from './client';
import { notificationKeys } from './notifications';

const enc = encodeURIComponent;

export interface AbsenceTodayQuery {
  site?: string;
  region?: string;
  date?: string;
}

export interface AbsenceScopeQuery {
  site?: string;
  region?: string;
}

export const absenceKeys = {
  me: ['absence', 'me'] as const,
  person: (personId: string) => ['absence', 'person', personId] as const,
  today: (q: AbsenceTodayQuery) => ['absence', 'today', q] as const,
  types: ['absence', 'types'] as const,
  overlap: (personId: string, q: object) => ['absence', 'overlap', personId, q] as const,
  approvals: ['absence', 'approvals'] as const,
  approval: (id: string) => ['absence', 'approvals', 'detail', id] as const,
  rtwOverdue: (q: AbsenceScopeQuery) => ['absence', 'rtw-overdue', q] as const,
  fitNotesDue: (q: AbsenceScopeQuery) => ['absence', 'fit-notes-due', q] as const,
};

export function getMyAbsence(): Promise<MyAbsenceResponse> {
  return apiGet('/absence/me', MyAbsenceResponseSchema);
}

export function getPersonAbsence(personId: string): Promise<AbsenceTimeline> {
  return apiGet(`/absence/people/${enc(personId)}`, AbsenceTimelineSchema);
}

export function getAbsenceToday(q: AbsenceTodayQuery): Promise<AbsenceTodayResponse> {
  return apiGet('/absence/today', AbsenceTodayResponseSchema, { ...q });
}

export function getAbsenceTypes(): Promise<AbsenceTypesResponse> {
  return apiGet('/absence/types', AbsenceTypesResponseSchema);
}

export function useMyAbsence(enabled = true) {
  return useQuery({ queryKey: absenceKeys.me, queryFn: getMyAbsence, enabled });
}

export function usePersonAbsence(personId: string, enabled = true) {
  return useQuery({
    queryKey: absenceKeys.person(personId),
    queryFn: () => getPersonAbsence(personId),
    enabled: enabled && personId.length > 0,
  });
}

export function useAbsenceToday(q: AbsenceTodayQuery, enabled = true) {
  return useQuery({
    queryKey: absenceKeys.today(q),
    queryFn: () => getAbsenceToday(q),
    enabled,
  });
}

export function useAbsenceTypes(enabled = true) {
  return useQuery({
    queryKey: absenceKeys.types,
    queryFn: getAbsenceTypes,
    enabled,
    staleTime: 300_000,
  });
}

// M2 ----------------------------------------------------------------------------------------

/** Everything an absence change can move: timelines, holidays (returned days), Home, the bell. */
export function useInvalidateAbsence() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['absence'] });
    void qc.invalidateQueries({ queryKey: ['holidays'] });
    void qc.invalidateQueries({ queryKey: ['home', 'tasks'] });
    void qc.invalidateQueries({ queryKey: ['queues'] });
    void qc.invalidateQueries({ queryKey: notificationKeys.list });
  };
}

export interface OverlapQuery {
  startDate: string;
  endDate?: string;
  expectedReturnOn?: string;
}

export function getHolidayOverlap(
  personId: string,
  q: OverlapQuery,
): Promise<HolidayOverlapPreview> {
  return apiGet(`/absence/people/${enc(personId)}/holiday-overlap`, HolidayOverlapPreviewSchema, {
    ...q,
  });
}

/** What recording would do to the person's approved holiday (the warning before saving). */
export function useHolidayOverlap(personId: string, q: OverlapQuery | null) {
  return useQuery({
    queryKey: absenceKeys.overlap(personId, q ?? {}),
    queryFn: () => getHolidayOverlap(personId, q!),
    enabled: q !== null && personId.length > 0,
    staleTime: 30_000,
    retry: false,
  });
}

export function recordAbsence(
  personId: string,
  body: AbsenceRecordCreate,
): Promise<AbsenceWriteResponse> {
  return apiPost(`/absence/people/${enc(personId)}`, AbsenceWriteResponseSchema, body);
}

export function closeAbsence(id: string, body: AbsenceClose): Promise<AbsenceWriteResponse> {
  return apiPost(`/absence/${enc(id)}/close`, AbsenceWriteResponseSchema, body);
}

export function patchAbsence(id: string, body: AbsencePatch): Promise<AbsenceWriteResponse> {
  return apiPatch(`/absence/${enc(id)}`, AbsenceWriteResponseSchema, body);
}

export function cancelAbsence(id: string, reason: string): Promise<AbsenceWriteResponse> {
  return apiPost(`/absence/${enc(id)}/cancel`, AbsenceWriteResponseSchema, { reason });
}

export function putReturnToWork(id: string, body: ReturnToWorkPut): Promise<AbsenceWriteResponse> {
  return apiPut(`/absence/${enc(id)}/rtw`, AbsenceWriteResponseSchema, body);
}

export function useRecordAbsence() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { personId: string; body: AbsenceRecordCreate }) =>
      recordAbsence(v.personId, v.body),
    onSuccess: invalidate,
  });
}

export function useCloseAbsence() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; body: AbsenceClose }) => closeAbsence(v.id, v.body),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

export function usePatchAbsence() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; body: AbsencePatch }) => patchAbsence(v.id, v.body),
    onSuccess: invalidate,
  });
}

export function useCancelAbsence() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; reason: string }) => cancelAbsence(v.id, v.reason),
    onSuccess: invalidate,
  });
}

export function usePutReturnToWork() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; body: ReturnToWorkPut }) => putReturnToWork(v.id, v.body),
    onSuccess: invalidate,
  });
}

// Requests and approvals -----------------------------------------------------------------------

export function createAbsenceRequest(body: AbsenceRequestCreate): Promise<AbsenceRequestResponse> {
  return apiPost('/absence/requests', AbsenceRequestResponseSchema, body);
}

export function withdrawAbsenceRequest(id: string) {
  return apiPost(`/absence/requests/${enc(id)}/withdraw`, AbsenceWithdrawResponseSchema);
}

export function getAbsenceApprovals(): Promise<AbsenceApprovalsResponse> {
  return apiGet('/absence/approvals', AbsenceApprovalsResponseSchema);
}

export function getAbsenceApprovalDetail(id: string): Promise<AbsenceApprovalDetail> {
  return apiGet(`/absence/approvals/${enc(id)}`, AbsenceApprovalDetailSchema);
}

export function approveAbsenceRequest(id: string, note?: string): Promise<AbsenceDecisionResponse> {
  return apiPost(`/absence/approvals/${enc(id)}/approve`, AbsenceDecisionResponseSchema, {
    ...(note ? { note } : {}),
  });
}

export function declineAbsenceRequest(id: string, note: string): Promise<AbsenceDecisionResponse> {
  return apiPost(`/absence/approvals/${enc(id)}/decline`, AbsenceDecisionResponseSchema, { note });
}

export function useCreateAbsenceRequest() {
  const invalidate = useInvalidateAbsence();
  return useMutation({ mutationFn: createAbsenceRequest, onSuccess: invalidate });
}

export function useWithdrawAbsenceRequest() {
  const invalidate = useInvalidateAbsence();
  return useMutation({ mutationFn: withdrawAbsenceRequest, onSuccess: invalidate });
}

/** Pending absence requests the signed-in person may decide (oldest first). */
export function useAbsenceApprovals(enabled = true) {
  return useQuery({
    queryKey: absenceKeys.approvals,
    queryFn: getAbsenceApprovals,
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useAbsenceApprovalDetail(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: absenceKeys.approval(id ?? ''),
    queryFn: () => getAbsenceApprovalDetail(id ?? ''),
    enabled: enabled && typeof id === 'string' && id.length > 0,
    retry: false,
  });
}

export function useApproveAbsenceRequest() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; note?: string }) => approveAbsenceRequest(v.id, v.note),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

export function useDeclineAbsenceRequest() {
  const invalidate = useInvalidateAbsence();
  return useMutation({
    mutationFn: (v: { id: string; note: string }) => declineAbsenceRequest(v.id, v.note),
    onSuccess: invalidate,
    onError: invalidate,
  });
}

// Attention lists ------------------------------------------------------------------------------

export function getRtwOverdue(q: AbsenceScopeQuery): Promise<AbsenceAttentionResponse> {
  return apiGet('/absence/rtw-overdue', AbsenceAttentionResponseSchema, { ...q });
}

export function getFitNotesDue(q: AbsenceScopeQuery): Promise<AbsenceAttentionResponse> {
  return apiGet('/absence/fit-notes-due', AbsenceAttentionResponseSchema, { ...q });
}

export function useRtwOverdue(q: AbsenceScopeQuery, enabled = true) {
  return useQuery({
    queryKey: absenceKeys.rtwOverdue(q),
    queryFn: () => getRtwOverdue(q),
    enabled,
  });
}

export function useFitNotesDue(q: AbsenceScopeQuery, enabled = true) {
  return useQuery({
    queryKey: absenceKeys.fitNotesDue(q),
    queryFn: () => getFitNotesDue(q),
    enabled,
  });
}
